import React, { useState, useEffect } from 'react';
import { useIntl } from 'umi';
import classnames from 'classnames';
import { SearchBox, AutoComplete, SelectOptionWithGroup } from 'tea-component';
import { useThrottleFn } from 'ahooks';
import { getTextWithHighlight } from '@/pages/intelligence/utils/commonUtils';

import type { IKeywordsSelectProps } from '../../types';

import './index.scss';

export default function KeywordsSelect(props: IKeywordsSelectProps) {
  const { formatMessage } = useIntl();

  const {
    searchPlaceholder = formatMessage({ id: 'dbi_search_any_game' }),
    keywordsFetcher,
    value,
    onChange,
    entityType,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [keyword, setKeyword] = useState(value || '');
  const [keywords, setKeywords] = useState<SelectOptionWithGroup[]>([]);

  const handleOpenChange = (payload: boolean) => {
    setIsOpen(payload);
  };

  const updateKeywords = async (payload: string) => {
    try {
      const data = await keywordsFetcher?.({
        entity_type: entityType,
        search_by_name: payload,
      });
      const list = Array.isArray(data) ? data : [];
      const searchResult = list?.find((item) => item?.genre_type === 'search');
      if (searchResult) {
        const resultList = searchResult?.list || [];
        const keywordsTemp = resultList?.map((item) => ({
          id: item.id,
          value: item.entity_name,
          text: getTextWithHighlight(item.entity_name, payload),
        }));
        setKeywords(keywordsTemp);
      } else {
        setKeywords([]);
      }
    } catch (exception) {
      // 这里出现异常，不重置Keywords为空，因为保留上一次搜索到的关键字数据，用户还可以继续选择，清空了后，就直接不能选择了
    }
  };

  const { run: updateKeywordsRun } = useThrottleFn(updateKeywords, {
    wait: 300,
    leading: true,
    trailing: true,
  });

  const handleKeywordChange = (payload: string) => {
    if (!payload) {
      setKeyword('');
      setKeywords([]);
      setIsOpen(false);
      onChange?.({ id: '', keyword: '' });
      return;
    }
    setKeyword(payload);
    updateKeywordsRun?.(payload);
  };

  const handleKeywordSelect = (payload?: string) => {
    const keywordTemp = payload || '';
    const targetGame: any = keywords?.find(
      (item) => item.value === keywordTemp,
    );
    if (targetGame) {
      setKeyword(keywordTemp);
      setIsOpen(false);
      onChange?.({ id: targetGame?.id, keyword: keywordTemp });
    } else {
      setKeyword(keywordTemp);
      setIsOpen(false);
      onChange?.({ id: '', keyword: keywordTemp });
    }
  };

  useEffect(() => {
    if (value !== keyword) {
      const valueTemp = value || '';
      setKeyword(valueTemp);
      handleKeywordChange(valueTemp);
    }
  }, [value]);

  return (
    <div className="dbi-mobile-compare-keyword-select">
      <AutoComplete
        open={!!keyword && isOpen}
        // open={true}
        placementOffset={0}
        overlayClassName={classnames(
          'dbi-mobile-compare-keyword-select-overlay',
          {
            empty: !keywords?.length,
          },
        )}
        options={keywords}
        onOpenChange={handleOpenChange}
        onChange={handleKeywordSelect}
      >
        {(ref) => (
          <SearchBox
            ref={ref}
            placeholder={searchPlaceholder}
            value={keyword}
            onSearch={handleKeywordSelect}
            onChange={handleKeywordChange}
          />
        )}
      </AutoComplete>
    </div>
  );
}
